import { Flex, Image, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import React from "react";

import theme from "theme/themeThree";
import { EmojiSelector } from "MVVM/Components/EmoijSelector";
import { BaseSection } from "../styledComponents";
import { TLoadout } from "MVVM/Views/CreateMontage/@types";
import { DropdownSelector } from "MVVM/Components/RiotRegionSelector/DropdownSelector";
import { InputText } from "MVVM/Components/Inputs";
import { Divider } from "MVVM/Components/Divider";
import { Button } from "MVVM/Components/Button";
import { getMomentGameName } from "MVVM/Utilities/Maps";
import { QuickEditPreferredLoadout } from "MVVM/State/quickEdit";
import { analytics } from "MVVM/Analytics";
import { IQuickEditMoment } from "../@types";
import { TQuickEditType } from "..";

interface ITitleAndLoadoutsProps {
  moment: IQuickEditMoment;
  selectedLoadout: TLoadout | null;
  setSelectedLoadout: (loadout: TLoadout) => void;
  title: string;
  setTitle: (title: string) => void;
  loadouts: TLoadout[];
  type: TQuickEditType;
}

export const TitleAndLoadouts = ({
  moment,
  selectedLoadout,
  setSelectedLoadout,
  title,
  setTitle,
  loadouts,
  type,
}: ITitleAndLoadoutsProps) => {
  const history = useHistory();
  const setPreferredLoadout = useSetRecoilState(QuickEditPreferredLoadout);

  const handleEmojiSelect = (emoji: string) => {
    setTitle(title + emoji);
  };

  const databaseLoadouts =
    loadouts?.map((loadout) => ({
      valueToDisplay: (
        <LoadoutOption key={loadout.name}>
          <LoadoutImage
            src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/${loadout.image}`}
          />
          <Text variant="text.labelSmall">{loadout.name}</Text>
        </LoadoutOption>
      ),
      valueToSave: loadout.name,
    })) ?? [];

  const values = [
    StudioLoadoutOption,
    RandomLoadoutOption,
    NoLoadoutOption,
    ...databaseLoadouts,
  ];

  return (
    <>
      <TitleSection>
        <Text
          variant="text.labelSmall"
          style={{
            width: "100%",
            justifyContent: "start",
            color: theme.colors.chalk,
          }}
        >
          Add a Title:
        </Text>
        <TitleInputContainer>
          <StyledInput
            value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTitle(e.target.value)
            }
            placeholder="Enter a custom clip title, or we'll create one for you."
          />
          <EmojiButton>
            <EmojiSelector
              onEmojiSelect={handleEmojiSelect}
              position="bottom"
            />
          </EmojiButton>
        </TitleInputContainer>
      </TitleSection>
      <LoadoutSection>
        <DropdownSelector
          values={values}
          inline={false}
          disabled={false}
          onChange={(value) => {
            if (value === "USE_STUDIO_LOADOUT") {
              setSelectedLoadout(StudioLoadout);
              setPreferredLoadout(StudioLoadout);
            } else if (value === "SURPRISE_ME_LOADOUT") {
              setPreferredLoadout(SurpriseMeLoadout);
              setSelectedLoadout(SurpriseMeLoadout);
            } else if (value === "NO_LOADOUT") {
              setSelectedLoadout(NoLoadout);
              setPreferredLoadout(NoLoadout);
            } else {
              const loadout = loadouts?.find(
                (loadout) => loadout.name === value,
              );
              if (loadout) {
                setPreferredLoadout(loadout);
                setSelectedLoadout(loadout);
              }
            }
          }}
          labelText={
            selectedLoadout ? (
              <LoadoutOption>
                <LoadoutImage
                  src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/${selectedLoadout.image}`}
                />
                <Text variant="text.labelSmall">{selectedLoadout.name}</Text>
              </LoadoutOption>
            ) : (
              <LoadoutOption>
                <LoadoutImage
                  src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/signatures/no_loadouts3.png`}
                />
                <Text variant="text.labelSmall">No Loadout</Text>
              </LoadoutOption>
            )
          }
          noSelection={true}
        />

        {type === "MOMENT" && (
          <>
            <Divider width={"100%"} my={0}>
              <Text
                variant="text.labelSmall"
                style={{ color: theme.colors.chalkAlpha40 }}
              >
                OR
              </Text>
            </Divider>
            <Button
              classification="secondary"
              onClick={() => {
                analytics.track("Quick Edit Select Custom Loadout", {});
                history.push(
                  `/remix/${moment.id}?game=["${getMomentGameName(
                    moment.game,
                  )}"]&next=${window.location.pathname}`,
                );
              }}
            >
              <Text variant="text.labelSmall">Choose a Custom Loadout</Text>
            </Button>
          </>
        )}
      </LoadoutSection>
    </>
  );
};

const StyledInput = styled(InputText)`
  ${theme.text.labelSmall}

  ::placeholder {
    color: ${(props) => props.theme.colors.chalkAlpha40};
  }
  background-color: ${(props) => props.theme.colors.grey825};
  border: none;
  flex: 1;
  padding-right: 40px;
`;

const TitleInputContainer = styled(Flex)`
  align-items: center;
  position: relative;
  width: 100%;
`;

const EmojiButton = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  position: absolute;
  right: 12px;
  z-index: 1;
`;

const LoadoutOption = styled(Flex)`
  align-items: center;
  gap: 12px;
  justify-content: start;
  width: 100%;
`;

const LoadoutImage = styled(Image)`
  width: 80px;
`;

const TitleSection = styled(BaseSection)`
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  width: 100%;
  padding: 24px;
`;

const LoadoutSection = styled(BaseSection)`
  align-items: start;
  border-bottom: none;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  width: 100%;
  padding: 0 24px 24px;
`;
const StudioLoadout = {
  name: "Use My Studio Cards",
  image: "web/signatures/envyLoadout.png",
} as TLoadout;

export const NoLoadout = {
  name: "No Loadout",
  image: "web/signatures/no_loadouts3.png",
} as TLoadout;

const SurpriseMeLoadout = {
  name: "Surprise Me",
  image: "web/signatures/random.png",
} as TLoadout;

const StudioLoadoutOption = {
  valueToDisplay: (
    <LoadoutOption>
      <LoadoutImage
        src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/signatures/envyLoadout.png`}
      />
      <Text variant="text.labelSmall">Use My Studio Cards</Text>
    </LoadoutOption>
  ),
  valueToSave: "USE_STUDIO_LOADOUT",
};

const RandomLoadoutOption = {
  valueToDisplay: (
    <LoadoutOption>
      <LoadoutImage
        src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/signatures/random.png`}
      />
      <Text variant="text.labelSmall">Surprise Me</Text>
    </LoadoutOption>
  ),
  valueToSave: "SURPRISE_ME_LOADOUT",
};

const NoLoadoutOption = {
  valueToDisplay: (
    <LoadoutOption>
      <LoadoutImage
        src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/signatures/no_loadouts3.png`}
      />
      <Text variant="text.labelSmall">No Loadout</Text>
    </LoadoutOption>
  ),
  valueToSave: "NO_LOADOUT",
};
